import {
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useEffect, useMemo, useState } from "react";
import { NumericFormatCustom } from "components/inputs/custom-numeric-input";
import { moneyFormat, unitFormat } from "utils/money-format";
import { neutral, primary } from "style/color";
import { useCreate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";

interface ModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id: number;
  name: string;
  amount: number;
  comment?: string;
  data: {
    idFund: number;
    name: string;
    reference: any;
    amount: number;
    ratio: number;
  }[];
  distributionDate: string;
  referenceDate: string;
}

export const GvDistributionConfirmationModal: React.FC<ModalProps> = ({
  open,
  setOpen,
  id,
  name,
  amount,
  data,
  distributionDate,
  referenceDate,
  comment
}) => {
  const [distributionData, setDistributionData] = useState<
    {
      idFund: number;
      name: string;
      reference: string;
      dpi: number;
      ratio: number;
      value: number;
      amount: number;
    }[]
  >([]);
  const [error, setError] = useState({ error: false, message: "" });

  const { mutate, isLoading } = useCreate();

  const navigate = useNavigate();
  useEffect(() => {
    if (data && data?.length > 0) {
      const updatedData = data.map((fund) => {
        const roundedAmount = Math.round((fund.ratio * amount + Number.EPSILON) * 100) / 100;
        const dpi = fund.amount > 0 ? Math.round((roundedAmount / fund.amount + Number.EPSILON) * 1000) / 1000 : 0;

        return {
          idFund: fund.idFund,
          name: fund.name,
          amount: fund.amount,
          reference: fund.reference,
          ratio: fund.ratio,
          value: roundedAmount,
          dpi: dpi,
        };
      });
      setDistributionData(updatedData);
    }
  }, [amount, data]);

  const sumAmount = useMemo(() => {
    const total = distributionData.reduce((prev, next) => prev + next.value, 0);
    const rounded =
      Math.round((total + Number.EPSILON) * 100) / 100;
    return rounded;
  }, [distributionData]);

  const diffAmount = useMemo(
    () =>
      Math.round((amount - sumAmount + Number.EPSILON) * 100) / 100,
    [amount, sumAmount]
  );


  const updateValue = (id: number, newValue: number, reinvest: boolean) => {
    const items = [...distributionData];
    const fund = distributionData.find((fund) => fund.idFund === id);
    const roundValue = Math.round((newValue + Number.EPSILON) * 100) / 100;
    if (fund) {
      fund.value = roundValue;
      fund.dpi = Math.round((roundValue / fund?.amount + Number.EPSILON) * 1000) / 1000;
    }

    setDistributionData(items);
  };
  return (
    <Dialog open={open} fullWidth>
      <DialogTitle color="primary">
        Add  distribution for {name}{" "}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography pb={1}>
          Are you sure you want to distribute the{" "}
          <b>
            {" "}
            {`${amount}`} {"dollars"}
          </b>{" "}
          across these funds{" "} as
          follow?
        </Typography>
        <Grid item xs={12}>
          {distributionData.length > 0
            ? distributionData.map((fund, index) => (
              <Grid
                key={index}
                sx={{ backgroundColor: "#f7f3f1", p: 1, my: 1 }}
                alignContent={"center"}
              >
                <Typography color={primary[500]}>{`${index + 1}- ${fund.name
                  } ${fund.reference ? `(Ref:${fund.reference})` : ""
                  }`}</Typography>
                <Grid container rowSpacing={1} columnGap={0.5}>
                  <Grid item sm={3}>
                    <TextField
                      label={"Amounts granted"}
                      value={moneyFormat(fund.amount) || 0}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any,
                      }}
                      disabled
                    />
                  </Grid>
                  <Grid item sm={2}>
                    <TextField
                      label={"Ratio"}
                      value={fund.ratio || 0}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any,
                      }}
                      disabled
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      label={`Distributed amount`}
                      value={moneyFormat(fund.value) || 0}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any,
                      }}
                      onChange={(event) => {
                        updateValue(
                          fund.idFund,
                          parseFloat(event.target.value),
                          false
                        );
                        setError({ error: false, message: "" });
                      }}
                    />
                  </Grid>
                  <Grid item sm={3}>
                    <TextField
                      label={`DPI`}
                      value={unitFormat(fund.dpi) || 0}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any,
                      }}
                      disabled
                    />
                  </Grid>

                </Grid>
              </Grid>
            ))
            : `No funds.`}
          <>
            <Grid>
              <Grid container rowSpacing={1} columnGap={0.5}>
                <Grid item sm={5} alignContent={"center"}>
                  <Typography color={primary[500]}>Total Entered</Typography>
                </Grid>

                <Grid item sm={3}>
                  <TextField
                    label={`Total amount`}
                    value={sumAmount}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    disabled
                  />
                </Grid>

              </Grid>
            </Grid>
            <Grid>
              <Grid container rowSpacing={1} columnGap={0.5}>
                <Grid item sm={5} alignContent={"center"}>
                  <Typography color={primary[500]}>Target</Typography>
                </Grid>

                <Grid item sm={3}>
                  <TextField
                    label={`Target amount`}
                    value={amount}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    disabled
                  />
                </Grid>

              </Grid>
            </Grid>
            <Grid>
              <Grid container rowSpacing={1} columnGap={0.5}>
                <Grid item sm={5} alignContent={"center"}>
                  <Typography color={primary[500]}>Delta</Typography>
                </Grid>

                <Grid item sm={3}>
                  <TextField
                    label={`Difference`}
                    value={diffAmount}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    disabled
                  />
                </Grid>

              </Grid>
            </Grid>
          </>
        </Grid>
        {error.error && (
          <Typography pb={1} sx={{ color: "red" }} variant="bodyCopyRegular">
            {error.message}
          </Typography>
        )}
        <br></br>

      </DialogContent>
      <Divider />
      <DialogActions>
        <>
          <Typography
            pb={1}
            sx={{ color: neutral[600] }}
            variant="bodyCopyRegular"
          >
            Note: Distributed cash
            are rounded to <b>2 decimal</b>.
          </Typography>

          <Button
            variant="contained"
            startIcon={
              isLoading && <CircularProgress size={20} color="inherit" />
            }
            onClick={() => {
              const roundedSumAmount = Math.round((sumAmount + Number.EPSILON) * 100) / 100;

              if (roundedSumAmount !== amount) {
                setError({
                  error: true,
                  message: `The total distributed Cash must equal ${amount}`,
                });
              }

              if (
                roundedSumAmount === amount
              ) {
                mutate(
                  {
                    resource: "fund-gv-distribution",
                    values: {
                      amount: amount,
                      grantvestmentId: id,
                      distributionDate: distributionDate,
                      referenceDate: referenceDate,
                      comment: comment,
                      distributions: distributionData,
                    },
                  },
                  {
                    onSuccess: (data) => {
                      navigate(-1);
                    },
                  }
                );

                setError({ error: false, message: "" });
                setOpen(false);
              }
            }}
            disabled={error.error}
          >
            Confirm
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setOpen(false);
              setError({ error: false, message: "" });
            }}
          >
            Cancel
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};
